import { useEffect, useState, useCallback } from 'react'
import { apiNewsList } from '../server/ServiceCall'
import { AuthState } from '../store/auth'

interface NewsRow {
  NEWS_CODE:  number
  NEWS_TYPE:  string
  CLUB_CODE:  number | null
  USER_CODE:  number | null
  HEADLINE:   string
  BODY:       string | null
  IMPORTANCE: number   // 1~5
  SEASON_NO:  number
  ROUND_NO:   number
  REG_DT:     string
}

type TypeFilter = 'all' | 'MATCH' | 'TRANSFER' | 'INJURY' | 'CLUB' | 'ECON' | 'SEASON'

// 뉴스 유형 → 한글 라벨 + 배지 색상 클래스
const TYPE_KO: Record<string, string> = {
  MATCH: '경기', TRANSFER: '이적', INJURY: '부상',
  CLUB: '구단', ECON: '경제', SEASON: '시즌',
}

const FILTERS: { key: TypeFilter; label: string }[] = [
  { key: 'all',      label: '전체' },
  { key: 'MATCH',    label: '경기' },
  { key: 'TRANSFER', label: '이적' },
  { key: 'INJURY',   label: '부상' },
  { key: 'CLUB',     label: '구단' },
  { key: 'ECON',     label: '경제' },
  { key: 'SEASON',   label: '시즌' },
]

interface Props {
  auth:   AuthState
  nonce?: number  // 부모가 변경하면 강제 재조회
}

export default function News({ auth, nonce }: Props) {
  const [rows,    setRows]    = useState<NewsRow[]>([])
  const [loading, setLoading] = useState(true)
  const [err,     setErr]     = useState('')
  const [filter,  setFilter]  = useState<TypeFilter>('all')

  const reload = useCallback(async () => {
    setLoading(true); setErr('')
    try {
      const r = await apiNewsList(auth.token, 50)
      if (r.resultCode !== '00') { setErr(r.resultMessage || '뉴스 조회 실패'); return }
      const o = JSON.parse(r.resultData) as { news: NewsRow[] }
      setRows(o.news ?? [])
    } catch (e: any) {
      setErr(e?.message ?? '네트워크 오류')
    } finally { setLoading(false) }
  }, [auth.token])

  useEffect(() => { reload() }, [reload, nonce])

  if (loading) return <div className="page-pad">뉴스 불러오는 중…</div>
  if (err)     return <div className="page-pad msg">{err}</div>

  const filteredRows = rows.filter(r =>
    filter === 'all' ? true : r.NEWS_TYPE === filter
  )

  return (
    <div className="page-pad news-page">
      <div className="news-head">
        <h2>뉴스</h2>
        <div className="news-filter">
          {FILTERS.map(f => (
            <button
              key={f.key}
              className={filter === f.key ? 'on' : ''}
              onClick={() => setFilter(f.key)}
            >{f.label}</button>
          ))}
        </div>
      </div>

      <section className="news-feed">
        {filteredRows.map(n => (
          <article key={n.NEWS_CODE} className={`news-item imp-${n.IMPORTANCE}`}>
            <div className="news-item-head">
              <span className={`news-type-badge nt-${n.NEWS_TYPE}`}>{TYPE_KO[n.NEWS_TYPE] ?? n.NEWS_TYPE}</span>
              {n.IMPORTANCE >= 4 && <span className="news-hot">중요</span>}
              {n.USER_CODE != null && <span className="news-mine">내 구단</span>}
              <span className="news-meta">시즌 {n.SEASON_NO} · {n.ROUND_NO}R · {n.REG_DT?.slice(0, 16)}</span>
            </div>
            <h3 className="news-headline">{n.HEADLINE}</h3>
            {n.BODY && <p className="news-body">{n.BODY}</p>}
          </article>
        ))}
        {filteredRows.length === 0 && (
          <div className="news-empty muted">표시할 뉴스가 없습니다. 라운드를 진행하면 새 소식이 도착합니다.</div>
        )}
      </section>
    </div>
  )
}
